import React from 'react';
import { useNavigate } from 'react-router-dom';
import FatigueSettings from '../components/FatigueSettings';
import ComplianceFooter, { ComplianceBadge } from '../components/ComplianceFooter';
import { Btn, Card, Note, PageBand, SectionHead, T } from '../components/ui';
import { useEmailManager } from '../index';

/**
 * `/admin/email/settings` — where the board's Settings button goes.
 *
 * Every one of the three frames carries its own Settings button, which is the clearest signal on
 * the board that these are separate pages: three buttons scrolling to the same place on one long
 * page would be pointless. The board never draws the destination, so this is it — the anti-fatigue
 * cap, the segment thresholds and the billing lead time, which are the settings the rest of the
 * screen reads.
 *
 * The compliance footer is here too, and this docblock used to say the opposite — that it was
 * "deliberately not here", on the page that renders it. It moved from the Analytics page in the
 * reorganisation and the sentence explaining its absence stayed behind.
 *
 * It belongs here for the same reason the rest does: one field, one editor, so no two pages can
 * disagree about what every email carries at its foot.
 */
const Settings = () => {
  const navigate = useNavigate();
  const {
    policy,
    savePolicy,
    busy,
    isRunning,
    confirmation,
    refusal,
    failed,
    loadPolicy,
    runCountryCodeBackfill,
    countryCodeResult
  } = useEmailManager();

  return (
    <>
      <PageBand
        brand
        title="Settings"
        subtitle="The rules every send is evaluated against, and the footer every email carries"
        actions={
          <Btn variant="outline" onClick={() => navigate('/admin/email')}>
            Back to dashboard
          </Btn>
        }
      />

      <Card id="policy">
        <SectionHead icon="Sliders" title="Email Policy & Guardrails" />
        <FatigueSettings
          settings={policy}
          onSave={savePolicy}
          busy={busy}
          saving={isRunning('save-policy')}
          failed={failed('policy')}
          onRetry={loadPolicy}
        />

        {/* A save used to store the response and change nothing an operator could see. */}
        {confirmation('save-policy') ? (
          <Note style={{ color: T.green }}>{confirmation('save-policy')}</Note>
        ) : null}
        {refusal('save-policy') ? (
          <Note style={{ color: T.red }}>{refusal('save-policy')}</Note>
        ) : null}
      </Card>

      <Card id="compliance">
        <SectionHead
          icon="Shield"
          title="Compliance & Global Footer Configuration"
          right={<ComplianceBadge policy={policy} />}
        />
        <ComplianceFooter
          policy={policy}
          onSave={(patch) => savePolicy(patch, 'save-compliance')}
          busy={busy}
          saving={isRunning('save-compliance')}
        />

        {confirmation('save-compliance') ? (
          <Note style={{ color: T.green }}>{confirmation('save-compliance')}</Note>
        ) : null}
        {refusal('save-compliance') ? (
          <Note style={{ color: T.red }}>{refusal('save-compliance')}</Note>
        ) : null}
      </Card>

      <Card id="measurement-data">
        <SectionHead icon="Users" title="Measurement Data" />
        <Note>
          A fan can only be measured if their country is stored as a two-letter code. The profile
          picker writes a display name — &ldquo;Canada&rdquo; — so accounts created before this was
          derived have a country the measurement gate cannot read, and are counted in the{' '}
          <strong>unknown location</strong> figure on a campaign&rsquo;s report rather than in any
          rate.
        </Note>
        <Note>
          This runs by itself every fifteen minutes. Press the button to do it now — after a deploy,
          say — or to see which country names could not be matched. Pressing it twice is harmless:
          it only fills in a code that was missing and never changes the country a fan chose.
        </Note>

        <Btn
          icon="Refresh"
          onClick={runCountryCodeBackfill}
          disabled={busy || isRunning('backfill-country-codes')}
        >
          {isRunning('backfill-country-codes') ? 'Checking accounts…' : 'Fill in missing countries'}
        </Btn>

        {confirmation('backfill-country-codes') ? (
          <Note style={{ color: T.green }}>{confirmation('backfill-country-codes')}</Note>
        ) : null}
        {refusal('backfill-country-codes') ? (
          <Note style={{ color: T.red }}>{refusal('backfill-country-codes')}</Note>
        ) : null}

        {/*
          The unresolved names, which are the reason this button shows a result at all.

          Left alone rather than guessed at: a wrong country silently moves a fan between legal
          regimes, while a missing one only leaves them unmeasured. Five of these — Northern Cyprus,
          Ossetia, Somaliland, Tibet, Transnistria — have no ISO code and a live sovereignty dispute,
          and will never resolve. The rest are usually typos worth correcting on the account.
        */}
        {countryCodeResult?.unresolved?.length ? (
          <Note>
            <strong>Not matched to a country code:</strong>{' '}
            {countryCodeResult.unresolved
              .map((entry) => `${entry.name} (${entry.count})`)
              .join(', ')}
            . These accounts stay unmeasured rather than being assigned a country they may not be
            in.
          </Note>
        ) : null}
      </Card>

      <Note>
        Two things the Email Manager does not offer, and both are decisions rather than gaps.{' '}
        <strong>Uploading custom HTML</strong> would bypass the component library that keeps every
        email consistent and correct in Outlook, which is the guarantee the template system exists
        to provide — the Templates page carried a disabled button saying so, and a permanently
        disabled control is a worse way to record a decision than this sentence.{' '}
        <strong>Save Workspace</strong> would bundle template edits, filters and a schedule under
        one name; each of the three saves on its own today, so it is worth building only if
        operators ask for it.
      </Note>
      {/*
        This paragraph said the opposite until the engagement work shipped — that the three rates
        were deliberately excluded and would stay em dashes. They are measured now, and a note
        explaining why a working feature does not exist is worse than no note at all.
      */}
      <Note>
        Click rate and CTA conversion are measured, for the recipients it was lawful to measure.{' '}
        <strong>Open rate</strong> additionally needs open tracking switched on at the email
        provider, which is an account setting rather than something this dashboard can change. Any
        rate still reading as an em dash rather than <strong>0.0%</strong> is saying &ldquo;nobody
        was measured&rdquo;, not &ldquo;nobody opened it&rdquo; — check the unknown-location figure
        on the campaign&rsquo;s report, and the button above.
      </Note>
    </>
  );
};

export default Settings;
